import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, catchError, finalize, forkJoin, map, of, shareReplay, tap } from 'rxjs';
import { SesionService } from '@intelishs-group/session-core';
import { CatalogoApiService } from '@services/catalogo-api.service';
import { ClientesApiService } from '@services/clientes-api.service';
import { MENSAJE_SIN_SERVICIO } from '@services/transporte-api.service';
import { fechaCorta, hoyIso, pesos } from '@shared/formato';
import type { EmpresaPagadora, Facturadora, RazonSocialGlobal } from '@schemas/clientes';
import type {
  BaseTope,
  Cliente,
  ClienteEmpresa,
  ClienteFacturadora,
  DetalleRegistro,
  DetalleTipoNomina,
  Empresa,
  Esquema,
  GrupoCliente,
  RazonSocialCliente,
  Referencias,
  TipoNomina,
  Tope,
  UsoEsquema,
  VigenciaEsquema,
  VigenciaFacturadora,
} from '@schemas/catalogo';

/**
 * Las listas que llegan TOLERANTES: si su API no responde llegan vacias y la carga sigue. Son las de
 * nominaapi (registros patronales y referencias, nominas, empresas que le facturan a cada razon
 * social) y, de clientesapi, la lista global de razones sociales y el catalogo de facturadoras.
 */
export type ListaTolerante = 'registros' | 'nominas' | 'facturadoras' | 'razones' | 'emisoras';

/** Todo lo que necesitan Empresas, Esquemas, Clientes, Registros y la banda del espacio, en una carga. */
export interface Catalogos {
  readonly esquemas: readonly Esquema[];
  readonly clientes: readonly Cliente[];
  /** Los grupos de clientes (solo para ver y filtrar: no son un nivel de configuracion). */
  readonly grupos: readonly GrupoCliente[];
  /** Las razones sociales de los clientes (una por vinculo): a quien se le factura. */
  readonly razones: readonly RazonSocialCliente[];
  /** Las pagadoras y las facturadoras, unidas por `kidempresa` (`unirEmpresas`). */
  readonly empresas: readonly Empresa[];
  /** Las relaciones comerciales (cliente en empresa pagadora), delgadas. */
  readonly asignaciones: readonly ClienteEmpresa[];
  /** Las nominas (tipos de nomina) de cada cliente en cada empresa, con lo que solo se lee. */
  readonly nominas: readonly DetalleTipoNomina[];
  /** Las empresas que le facturan a cada razon social de los clientes (relaciones con la facturadora). */
  readonly facturadoras: readonly ClienteFacturadora[];
  readonly registros: readonly DetalleRegistro[];
  readonly refs: Referencias;
  /**
   * El mensaje de cada lista tolerante que no llego (sin la llave, llego). Las pantallas lo muestran
   * en lugar de una lista vacia que pareceria «no hay registros»: casi siempre es el mensaje estandar
   * de una ruta que el gateway aun no enruta. Sustituye a los booleanos `nomina` y
   * `nominaAsignaciones` de antes del Bloque 2 (`nomina` es ahora el mapa de nominas, como en `fnomina`).
   */
  readonly sinServicio: Readonly<Partial<Record<ListaTolerante, string>>>;
  readonly esquema: ReadonlyMap<number, Esquema>;
  readonly cliente: ReadonlyMap<number, Cliente>;
  readonly grupo: ReadonlyMap<number, GrupoCliente>;
  /** Por `id` (= `empresa_id`); si la razon social tuvo varios vinculos, el vigente. */
  readonly razon: ReadonlyMap<number, RazonSocialCliente>;
  readonly empresa: ReadonlyMap<number, Empresa>;
  readonly asignacion: ReadonlyMap<number, ClienteEmpresa>;
  readonly nomina: ReadonlyMap<number, DetalleTipoNomina>;
}

/**
 * Catalogos de grupos, empresas, esquemas, clientes, sus razones sociales, sus asignaciones, sus
 * nominas y las empresas que les facturan.
 *
 * Lo comercial viene de clientesapi y lo de nomina de nominaapi. Solo las listas comerciales de
 * siempre (grupos, esquemas, clientes, pagadoras y relaciones) tumban una carga estricta; las demas
 * llegan TOLERANTES (`ListaTolerante`: vacias si fallan, con su mensaje en `sinServicio`) para que
 * Clientes, Empresas y Esquemas funcionen sin ellas.
 *
 * `datos` guarda la ULTIMA carga para quien solo necesita nombres (la banda del
 * espacio, «Dónde está personalizado»). Las pantallas que editan llaman
 * `alEntrar()` al abrirse y `cargar()` despues de guardar o al recargar: la
 * lista que se edita siempre es la del servidor, nunca una copia que alguien
 * mas pudo cambiar.
 */
@Injectable({ providedIn: 'root' })
export class CatalogosService {
  private readonly api = inject(CatalogoApiService);
  private readonly clientesApi = inject(ClientesApiService);
  private readonly identidad = inject(SesionService);

  readonly datos = signal<Catalogos | null>(null);

  /**
   * De quien son `datos` (2026-10-09): cerrar sesion no recarga la pagina, asi que sin esto el
   * siguiente usuario veia en «Ir a…» y en «Cambiar» las empresas y nominas del anterior. Al cambiar
   * el usuario de la sesion se tira todo.
   */
  private dueno: string | null = null;

  /**
   * Las referencias (entidades, clases de riesgo, periodicidades) no cambian: una
   * sola consulta. Si falla, `shareReplay` se reinicia y la siguiente reintenta.
   */
  private readonly refs$ = this.api.referencias().pipe(shareReplay(1));

  /** La consulta en curso: la comparten quienes piden al mismo tiempo. */
  private enCurso: Observable<Respuestas> | null = null;

  /** Navegacion en curso y la ultima carga completa (sin listas fallidas) hecha en ella. */
  private navegacion = 0;
  private completa: { readonly navegacion: number; readonly datos: Catalogos } | null = null;

  constructor() {
    inject(Router).events.subscribe((e) => {
      if (e instanceof NavigationStart) this.navegacion = e.id;
    });
    effect(() => {
      this.identidad.usuario();
      untracked(() => this.revisarDueno());
    });
  }

  /** Los catalogos que hay son de quien tiene la sesion (y no de quien la tuvo antes). */
  deLaSesion(): boolean {
    this.revisarDueno();
    return this.datos() !== null;
  }

  /** Otro usuario en la sesion (o ninguno): se tiran los catalogos, la carga completa y la consulta en curso. */
  private revisarDueno(): void {
    const actual = this.identidad.usuario()?.id.toString() ?? null;
    if (actual === this.dueno) return;
    this.dueno = actual;
    this.datos.set(null);
    this.completa = null;
    this.enCurso = null;
  }

  referencias(): Observable<Referencias> {
    return this.refs$;
  }

  /**
   * Solo las empresas (pagadoras y facturadoras unidas), para las pantallas que no necesitan lo
   * demas (Registros patronales y Estructura). Las pagadoras son estrictas; si el catalogo de
   * facturadoras no responde, llegan solo las pagadoras.
   */
  empresas(): Observable<Empresa[]> {
    return forkJoin({
      pagadoras: this.clientesApi.empresas(),
      emisoras: this.clientesApi.facturadoras().pipe(catchError(() => of<Facturadora[]>([]))),
    }).pipe(map(({ pagadoras, emisoras }) => unirEmpresas(pagadoras, emisoras)));
  }

  /**
   * Con `tolerante`, un catalogo que falla llega vacio en lugar de tumbar la
   * carga: es lo que usa el marco del espacio, donde basta con los nombres que
   * si llegaron. Las pantallas que editan cargan estricto y muestran el error.
   */
  cargar(tolerante = false): Observable<Catalogos> {
    this.revisarDueno();
    const navegacion = this.navegacion;
    const dueno = this.dueno;
    return this.consulta().pipe(
      map((r) => {
        // Solo las listas comerciales de siempre (clientesapi) tumban una carga estricta.
        const fallo = [r.grupos, r.esquemas, r.clientes, r.pagadoras, r.asignaciones].find((x) => !x.ok);
        if (fallo && !tolerante) throw fallo.error;
        const sinServicio: Partial<Record<ListaTolerante, string>> = {};
        const tolerada = (lista: ListaTolerante, ...rs: readonly Uno<unknown>[]): void => {
          for (const x of rs) {
            if (!x.ok) {
              sinServicio[lista] = mensajeDe(x.error);
              return;
            }
          }
        };
        tolerada('registros', r.registros, r.refs);
        tolerada('nominas', r.nominas);
        tolerada('facturadoras', r.facturadoras);
        tolerada('razones', r.razones);
        tolerada('emisoras', r.emisoras);
        const c = {
          esquemas: valor(r.esquemas),
          clientes: valor(r.clientes),
          grupos: valor(r.grupos),
          razones: valor(r.razones)
            .map((g) => aRazonSocial(g))
            .filter((x): x is RazonSocialCliente => x !== null),
          empresas: unirEmpresas(valor(r.pagadoras), valor(r.emisoras)),
          asignaciones: valor(r.asignaciones),
          nominas: valor(r.nominas),
          facturadoras: valor(r.facturadoras),
          registros: valor(r.registros),
          refs: r.refs.ok ? r.refs.valor : REFERENCIAS_VACIAS,
        };
        const datos: Catalogos = {
          ...c,
          sinServicio,
          esquema: porId(c.esquemas),
          cliente: porId(c.clientes),
          grupo: porId(c.grupos),
          razon: porRazon(c.razones),
          empresa: porId(c.empresas),
          asignacion: porId(c.asignaciones),
          nomina: porId(c.nominas),
        };
        // Una respuesta pedida por el usuario anterior no se guarda para el nuevo.
        if (dueno === this.dueno) this.completa = fallo ? null : { navegacion, datos };
        return datos;
      }),
      tap((c) => {
        if (dueno === this.dueno) this.datos.set(c);
      }),
    );
  }

  /**
   * La carga de una pantalla que se abre. El marco del espacio ya consulto al
   * entrar (para saber si la empresa o el cliente existen) y la pantalla se
   * monta despues: si esa carga llego completa en esta misma navegacion, se usa
   * en lugar de volver a pedirla. Si no, consulta (estricto).
   */
  alEntrar(): Observable<Catalogos> {
    this.revisarDueno();
    const c = this.completa;
    return c && c.navegacion === this.navegacion ? of(c.datos) : this.cargar();
  }

  /**
   * Una sola consulta para quienes piden a la vez. Cada lista llega con su
   * error para que cada quien decida si es tolerante; al terminar se suelta y
   * la siguiente llamada vuelve a consultar al servidor.
   */
  private consulta(): Observable<Respuestas> {
    if (!this.enCurso) {
      this.enCurso = forkJoin({
        grupos: resultado(this.clientesApi.grupos()),
        esquemas: resultado(this.clientesApi.esquemas()),
        clientes: resultado(this.clientesApi.clientes()),
        pagadoras: resultado(this.clientesApi.empresas()),
        emisoras: resultado(this.clientesApi.facturadoras()),
        razones: resultado(this.clientesApi.razonesSocialesGlobal()),
        asignaciones: resultado(this.clientesApi.relaciones()),
        nominas: resultado(this.api.tiposNomina()),
        facturadoras: resultado(this.api.clientesFacturadoras()),
        registros: resultado(this.api.registrosPatronales()),
        refs: this.refs$.pipe(
          map((valor): Uno<Referencias> => ({ ok: true, valor })),
          catchError((error: unknown) => of<Uno<Referencias>>({ ok: false, error })),
        ),
      }).pipe(
        finalize(() => (this.enCurso = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.enCurso;
  }
}

type Uno<T> = { readonly ok: true; readonly valor: T } | { readonly ok: false; readonly error: unknown };
type Resultado<T> = Uno<T[]>;

interface Respuestas {
  readonly grupos: Resultado<GrupoCliente>;
  readonly esquemas: Resultado<Esquema>;
  readonly clientes: Resultado<Cliente>;
  /** Empresas pagadoras (clientesapi). */
  readonly pagadoras: Resultado<EmpresaPagadora>;
  /** El catalogo de facturadoras: las empresas emisoras activas (clientesapi). */
  readonly emisoras: Resultado<Facturadora>;
  /** La lista global de razones sociales (clientesapi). */
  readonly razones: Resultado<RazonSocialGlobal>;
  /** Relaciones comerciales (clientesapi). */
  readonly asignaciones: Resultado<ClienteEmpresa>;
  /** Las nominas (nominaapi, `GET /api/v1/tipos-nomina`). */
  readonly nominas: Resultado<DetalleTipoNomina>;
  /** Las empresas que le facturan a cada razon social (nominaapi, `GET /api/v1/clientes-facturadoras`). */
  readonly facturadoras: Resultado<ClienteFacturadora>;
  readonly registros: Resultado<DetalleRegistro>;
  readonly refs: Uno<Referencias>;
}

function resultado<T>(fuente: Observable<T[]>): Observable<Resultado<T>> {
  return fuente.pipe(
    map((valor): Resultado<T> => ({ ok: true, valor })),
    catchError((error: unknown) => of<Resultado<T>>({ ok: false, error })),
  );
}

/** Lo que llego; una lista que fallo llega vacia (solo la usa quien es tolerante). */
function valor<T>(r: Resultado<T>): T[] {
  return r.ok ? r.valor : [];
}

/** El texto del error tal como lo da la API (casi siempre, el mensaje estandar `SIN_SERVICIO`). */
function mensajeDe(e: unknown): string {
  return e instanceof Error && e.message ? e.message : MENSAJE_SIN_SERVICIO;
}

/** Sin el servicio de nomina: catalogos de referencia vacios. */
export const REFERENCIAS_VACIAS: Referencias = {
  entidades_federativas: { elementos: [] },
  clases_riesgo: { elementos: [] },
  periodicidades: { elementos: [] },
};

function porId<T extends { id: number }>(lista: readonly T[]): ReadonlyMap<number, T> {
  return new Map(lista.map((x) => [x.id, x]));
}

/** Una razon social reasignada sale una vez por vinculo: el mapa se queda con la vigente (o la ultima). */
function porRazon(lista: readonly RazonSocialCliente[]): ReadonlyMap<number, RazonSocialCliente> {
  const mapa = new Map<number, RazonSocialCliente>();
  for (const r of lista) {
    const otra = mapa.get(r.id);
    const mejor =
      !otra || (r.activo !== otra.activo ? r.activo : (r.vigente_desde ?? '') > (otra.vigente_desde ?? ''));
    if (mejor) mapa.set(r.id, r);
  }
  return mapa;
}

// ── Del contrato de clientesapi a lo que usan las pantallas ─────────────────

/**
 * Las empresas de las pantallas: las pagadoras (`empresas-pagadoras`) y las facturadoras (el
 * catalogo de emisoras activas), unidas por `kidempresa`. Una empresa interna suele ser las dos.
 *
 * - `pagadora` = habilitada como pagadora; una deshabilitada conserva su `tipo`.
 * - `facturadora` = esta en el catalogo de facturadoras.
 * - `activa` = la pagadora habilitada o, si factura, activa como facturadora.
 * - `razon_social_id` = en la maquila (`DEL_CLIENTE`), su propio id: la empresa de la maquila ES una
 *   razon social del cliente.
 *
 * Las que solo facturan llegan con su nombre y su RFC (sin domicilio ni registro de pagadora).
 */
export function unirEmpresas(pagadoras: readonly EmpresaPagadora[], emisoras: readonly Facturadora[]): Empresa[] {
  const facturan = new Set(emisoras.map((f) => f.id));
  const propias = new Set(pagadoras.map((p) => p.id));
  const lista: Empresa[] = pagadoras.map((p) => {
    const facturadora = facturan.has(p.id);
    return {
      ...p,
      pagadora: p.activa,
      facturadora,
      activa: p.activa || facturadora,
      razon_social_id: p.del_cliente ? p.id : null,
    };
  });
  for (const f of emisoras) if (!propias.has(f.id)) lista.push(soloFactura(f));
  return lista.sort((x, y) => x.razon_social.localeCompare(y.razon_social, 'es') || x.id - y.id);
}

function soloFactura(f: Facturadora): Empresa {
  return {
    id: f.id,
    razon_social: f.nombre,
    rfc: f.rfc,
    calle_numero: '',
    colonia: '',
    municipio: '',
    codigo_postal: '',
    entidad: '',
    entidad_id: null,
    registro_infonavit: '',
    representante_legal: '',
    esquemas: [],
    tipo: null,
    del_cliente: false,
    activa: true,
    empresa_activa: true,
    version: null,
    pagadora: false,
    facturadora: true,
    razon_social_id: null,
  };
}

/**
 * Una fila de la lista global de razones sociales → la razon social que usan las pantallas (`id` =
 * `empresa_id`; `activo` = el vinculo no ha terminado y la empresa esta activa). Las que no son de
 * ningun cliente (solo salen al filtrar por RFC) no se usan: `null`.
 */
export function aRazonSocial(g: RazonSocialGlobal, hoy = hoyIso()): RazonSocialCliente | null {
  if (g.cliente_id === null || g.vinculo_id === null) return null;
  const { activo: empresaActiva, ...resto } = g;
  return {
    ...resto,
    id: g.empresa_id,
    vinculo_id: g.vinculo_id,
    cliente_id: g.cliente_id,
    empresa_activa: empresaActiva,
    activo: empresaActiva && (!g.vigente_hasta || g.vigente_hasta >= hoy),
  };
}

// ── Textos y reglas de presentacion compartidos ────────────────────────────

export const USOS: Record<UsoEsquema, string> = {
  PRINCIPAL: 'Nómina principal',
  COMPLEMENTO: 'Complemento',
  AMBOS: 'Principal o complemento',
};

export const BASES_TOPE: Record<BaseTope, string> = {
  PAGO_TOTAL: 'del pago total al trabajador',
  RESTANTE: 'de lo que falta por pagar',
  SUELDO_PRINCIPAL: 'del sueldo de la nómina principal',
  PERCEPCIONES_PRINCIPAL: 'de las percepciones de la nómina principal',
};

export const BASES_TOPE_CORTAS: Record<BaseTope, string> = {
  PAGO_TOTAL: 'Pago total',
  RESTANTE: 'Lo que falta por pagar',
  SUELDO_PRINCIPAL: 'Sueldo de la principal',
  PERCEPCIONES_PRINCIPAL: 'Percepciones de la principal',
};

/** Lo que tiene esquemas por vigencia: una nomina o (sugeridos) la relacion comercial. */
interface ConEsquemas {
  readonly esquemas: readonly VigenciaEsquema[];
}

/** El esquema que rige hoy en la nomina: la vigencia mas reciente que ya empezo. */
export function esquemaHoy(a: ConEsquemas): VigenciaEsquema | null {
  const hoy = hoyIso();
  return [...a.esquemas].reverse().find((v) => v.vigente_desde <= hoy) ?? null;
}

/** El de hoy y los programados: los que revisa la API (los anteriores ya no rigen). */
export function esquemasDesdeHoy(a: ConEsquemas): number[] {
  const hoy = esquemaHoy(a);
  return a.esquemas.slice(hoy ? a.esquemas.indexOf(hoy) : 0).map((v) => v.esquema_id);
}

/** El que rige hoy o, si ninguno ha empezado, el primero programado (0 sin ninguno: sugeridos vacios). */
export function idEsquemaHoy(a: ConEsquemas): number {
  return (esquemaHoy(a) ?? a.esquemas[0])?.esquema_id ?? 0;
}

/** La facturadora que rige hoy en la nomina: la vigencia mas reciente que ya empezo. */
export function facturadoraHoy(a: TipoNomina): VigenciaFacturadora | null {
  const hoy = hoyIso();
  return [...(a.facturadoras ?? [])].reverse().find((v) => v.vigente_desde <= hoy) ?? null;
}

/**
 * La empresa que factura. La API solo manda las empresas al alcance del usuario y quien eligio la
 * facturadora pudo ver otra: si no llego, no se muestra su id.
 */
export function nombreFacturadora(c: Catalogos, id: number): string {
  return c.empresa.get(id)?.razon_social ?? 'Otra empresa (fuera de tu acceso)';
}

export type EstadoFactura = 'VIGENTE' | 'PROGRAMADA' | 'TERMINADA' | 'BAJA_PROGRAMADA';

/**
 * Donde esta hoy la relacion cliente-facturadora: programada (empieza despues), vigente,
 * vigente con baja futura o terminada (su ultimo dia ya paso).
 */
export function estadoFactura(r: ClienteFacturadora): { readonly estado: EstadoFactura; readonly texto: string } {
  const hoy = hoyIso();
  if (r.fecha_baja && r.fecha_baja < hoy) return { estado: 'TERMINADA', texto: 'Terminada' };
  if (r.vigente_desde > hoy) return { estado: 'PROGRAMADA', texto: 'Programada' };
  if (r.fecha_baja) {
    return { estado: 'BAJA_PROGRAMADA', texto: `De baja desde el ${fechaCorta(diaSiguiente(r.fecha_baja))}` };
  }
  return { estado: 'VIGENTE', texto: 'Vigente' };
}

/** Le factura hoy o le facturara despues: las que se pueden elegir en una nomina. */
export function facturaHoyODespues(r: ClienteFacturadora): boolean {
  return !r.fecha_baja || r.fecha_baja >= hoyIso();
}

/**
 * Una razon social y una empresa pueden tener varios tramos (relaciones que no se enciman en
 * fechas; 2026-10-07). Se puede «Reactivar» (dar de alta un tramo nuevo del mismo par) desde el
 * ultimo tramo terminado de un par que no tiene ninguno vigente ni programado.
 */
export function sePuedeReactivar(todas: readonly ClienteFacturadora[], r: ClienteFacturadora): boolean {
  if (estadoFactura(r).estado !== 'TERMINADA') return false;
  const par = todas.filter((x) => x.razon_social_id === r.razon_social_id && x.empresa_id === r.empresa_id);
  if (par.some(facturaHoyODespues)) return false;
  // Solo en el mas reciente del par, para no repetir la accion en cada tramo viejo.
  return !par.some((x) => x.id !== r.id && (x.fecha_baja ?? '') > (r.fecha_baja ?? ''));
}

/** El dia siguiente de una fecha AAAA-MM-DD, sin pasar por la zona horaria. */
export function diaSiguiente(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  const f = new Date(Date.UTC(a, m - 1, d + 1));
  return f.toISOString().slice(0, 10);
}

/**
 * A quien se le factura una vigencia de la nomina: «Facturadora → Razón social (RFC)» o «No se
 * factura» (`empresa_id` null). Una guardada antes de las razones sociales llega sin ella.
 */
export function nombreVigenciaFactura(c: Catalogos, v: VigenciaFacturadora): string {
  if (v.empresa_id === null) return 'No se factura';
  const empresa = nombreFacturadora(c, v.empresa_id);
  return v.razon_social_id === null ? empresa : `${empresa} → ${nombreRazonSocial(c, v.razon_social_id)}`;
}

/**
 * Las condiciones de la prefactura de una vigencia: «Servicio 7 % · IVA 16 %», con «Sin servicio» e
 * «IVA de ley» cuando no se capturaron. Tambien con «No se factura»: se cobran aunque no haya factura.
 */
export function textoCondicionesFactura(v: VigenciaFacturadora): string {
  const servicio = v.porcentaje_servicio ? `Servicio ${v.porcentaje_servicio} %` : 'Sin servicio';
  const iva = v.porcentaje_iva ? `IVA ${v.porcentaje_iva} %` : 'IVA de ley';
  return `${servicio} · ${iva}`;
}

/** La pareja de una relacion con la facturadora: «Facturadora → Razón social (RFC)». */
export function nombreRelacionFactura(c: Catalogos, r: ClienteFacturadora): string {
  return `${nombreFacturadora(c, r.empresa_id)} → ${nombreRazonSocial(c, r.razon_social_id)}`;
}

/**
 * «Razón social (RFC)». La API solo manda las razones sociales de los clientes al alcance del
 * usuario: si no llego, no se muestra su id.
 */
export function nombreRazonSocial(c: Catalogos, id: number | null | undefined): string {
  if (id === null || id === undefined) return '';
  const r = c.razon.get(id);
  return r ? `${r.razon_social} (${r.rfc})` : 'Otra razón social (fuera de tu acceso)';
}

/** Las razones sociales de un cliente: activas primero, luego por nombre. */
export function razonesDe(c: Catalogos, cliente: number): RazonSocialCliente[] {
  return c.razones
    .filter((r) => r.cliente_id === cliente)
    .sort((x, y) => Number(y.activo) - Number(x.activo) || x.razon_social.localeCompare(y.razon_social));
}

/** El nombre del grupo; vacio sin grupo. */
export function nombreGrupo(c: Catalogos, id: number | null | undefined): string {
  if (id === null || id === undefined) return '';
  return c.grupo.get(id)?.nombre ?? 'Otro grupo (fuera de tu acceso)';
}

/** La razon social que es una empresa del cliente (maquila), si la tiene y se ve. */
export function razonDeEmpresa(c: Catalogos, e: Empresa): RazonSocialCliente | null {
  return e.del_cliente && e.razon_social_id !== null ? (c.razon.get(e.razon_social_id) ?? null) : null;
}

/** Pagadora, facturadora o las dos; la del cliente (maquila) se distingue. */
export function clasificacionEmpresa(e: Empresa): string {
  if (e.pagadora && e.facturadora) return 'Pagadora y facturadora';
  if (e.pagadora) return e.del_cliente ? 'Pagadora · del cliente' : 'Pagadora';
  if (e.facturadora) return 'Facturadora';
  // En SINAY una pagadora deshabilitada conserva su tipo: ya no es pagadora.
  return e.tipo !== null ? 'Pagadora deshabilitada' : 'Sin clasificar';
}

/** Como se rotula una fila por vigencia: «Vigente hoy», «Programada» o las anteriores (en gris). */
export interface MarcaVigencia {
  readonly marca: string;
  /** Ya no rige: la fila va en gris (`sinay-fila-inactiva`). */
  readonly anterior: boolean;
  readonly vigente: boolean;
}

/** `desde`/`hasta` como `AAAA-MM-DD`; `hasta` null = abierta. */
export function marcaVigencia(desde: string, hasta: string | null | undefined, hoy = hoyIso()): MarcaVigencia {
  if (desde > hoy) return { marca: 'Programada', anterior: false, vigente: false };
  if (hasta && hasta < hoy) return { marca: `Terminó el ${fechaCorta(hasta)}`, anterior: true, vigente: false };
  return { marca: hasta ? `Vigente hoy · hasta el ${fechaCorta(hasta)}` : 'Vigente hoy', anterior: false, vigente: true };
}

export function textoTope(t: Tope | null): string {
  if (!t) return 'Sin tope: recibe lo que falte';
  const variable = t.modo === 'VARIABLE';
  if (t.tipo === 'MONTO') {
    return variable ? 'Hasta el monto que se capture en cada periodo' : `Hasta ${pesos(t.valor ?? 0)}`;
  }
  const base = t.base ? BASES_TOPE[t.base] : '';
  return `${variable ? 'Hasta el porcentaje que se capture en cada periodo' : `Hasta el ${t.valor} %`} ${base}`.trim();
}

export function nombreEsquema(c: Catalogos, id: number): string {
  return c.esquema.get(id)?.nombre ?? `Esquema ${id}`;
}

export function nombreEmpresa(c: Catalogos, id: number): string {
  return c.empresa.get(id)?.razon_social ?? `Empresa ${id}`;
}

/** «QNA-NORTE · Nombre de la nómina». */
export function textoNomina(t: Pick<TipoNomina, 'clave' | 'nombre'>): string {
  return t.nombre ? `${t.clave} · ${t.nombre}` : t.clave;
}

/**
 * Una nomina ligada en la cadena de pagos: su clave y su empresa. La API solo manda las nominas al
 * alcance del usuario: si la ligada no llego, es de una empresa fuera de su acceso (no se muestra su id).
 */
export function nombreLigada(c: Catalogos, id: number): string {
  const ligada = c.nomina.get(id);
  return ligada ? `${ligada.clave} · ${nombreEmpresa(c, ligada.empresa_id)}` : 'Otra nómina (fuera de tu acceso)';
}

export function nombreCliente(c: Catalogos, id: number): string {
  return c.cliente.get(id)?.nombre ?? `Cliente ${id}`;
}

export function nombrePeriodicidad(c: Catalogos, id: number): string {
  return c.refs.periodicidades.elementos.find((p) => p.id === id)?.nombre ?? `Periodicidad ${id}`;
}

/** Las nominas de un cliente en una empresa (sin filtro en lo que no se indica). */
export function nominasDe(c: Catalogos, filtro: { cliente?: number; empresa?: number }): DetalleTipoNomina[] {
  return c.nominas.filter(
    (t) =>
      (filtro.cliente === undefined || t.cliente_id === filtro.cliente) &&
      (filtro.empresa === undefined || t.empresa_id === filtro.empresa),
  );
}

export function nombreEntidad(refs: Referencias | null | undefined, id: number | null | undefined): string {
  if (id === null || id === undefined) return '';
  return refs?.entidades_federativas.elementos.find((e) => e.id === id)?.nombre ?? String(id);
}

/**
 * La nomina principal de la que `t` es complemento, y en que lugar de su cadena. La dice la API
 * (`complemento_de`) porque la principal puede estar en una empresa fuera del alcance del usuario:
 * entonces `principal` es null.
 */
export function principalDe(
  c: Catalogos,
  t: DetalleTipoNomina,
): { principal: DetalleTipoNomina | null; lugar: number } | null {
  const de = t.complemento_de;
  if (!de) return null;
  return { principal: de.principal_id === null ? null : (c.nomina.get(de.principal_id) ?? null), lugar: de.lugar };
}

/**
 * Cada cadena junta (la principal y despues sus complementos, en orden) y al final las demas; dentro de
 * cada grupo, por empresa y clave. Las de baja van al final.
 */
export function ordenarNominas(c: Catalogos, lista: readonly DetalleTipoNomina[]): DetalleTipoNomina[] {
  const porNombre = (x: DetalleTipoNomina, y: DetalleTipoNomina): number =>
    Number(y.activo) - Number(x.activo) ||
    nombreEmpresa(c, x.empresa_id).localeCompare(nombreEmpresa(c, y.empresa_id)) ||
    x.clave.localeCompare(y.clave);
  const ids = new Set(lista.map((t) => t.id));
  const salida: DetalleTipoNomina[] = [];
  const puestas = new Set<number>();
  for (const p of [...lista].filter((t) => t.complementos.length).sort(porNombre)) {
    salida.push(p);
    puestas.add(p.id);
    for (const k of p.complementos) {
      const ligada = c.nomina.get(k.tipo_nomina_id);
      if (ligada && ids.has(ligada.id) && !puestas.has(ligada.id)) {
        salida.push(lista.find((t) => t.id === ligada.id) ?? ligada);
        puestas.add(ligada.id);
      }
    }
  }
  return [...salida, ...lista.filter((t) => !puestas.has(t.id)).sort(porNombre)];
}
